import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { ClientFacingNotesField } from "@/components/invoice/ClientFacingNotesField";
import { PaymentQrOptionsField } from "@/components/invoice/PaymentQrOptionsField";
import { Button } from "@/components/ui/Button";
import { FieldLabel, Input } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import type { CurrencyCode, PartyInfo, PaymentQrAssetMap, PaymentQrSettings } from "@/domain/types";

interface Values {
  client: PartyInfo;
  serviceStartDate: string;
  serviceEndDate: string;
  publicNotes: string;
  paymentQr: PaymentQrSettings;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValues: Values;
  currency: CurrencyCode;
  paymentQrAssets: PaymentQrAssetMap;
  onSave: (values: Values) => Promise<void>;
}

export function MetadataDialog({
  open,
  onOpenChange,
  initialValues,
  currency,
  paymentQrAssets,
  onSave,
}: Props) {
  const [values, setValues] = useState(initialValues);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setValues(structuredClone(initialValues));
    }
  }, [initialValues, open]);

  const setClient = (
    field: keyof Pick<
      PartyInfo,
      "name" | "company" | "email" | "phone"
    >,
    value: string,
  ) =>
    setValues((current) => ({
      ...current,
      client: { ...current.client, [field]: value },
    }));

  const submit = async () => {
    if (!values.client.name.trim()) {
      window.alert("Client name is required.");
      return;
    }

    if (
      Boolean(values.serviceStartDate) !==
      Boolean(values.serviceEndDate)
    ) {
      window.alert(
        "Enter both service dates, or leave both blank.",
      );
      return;
    }

    if (
      values.serviceStartDate &&
      values.serviceEndDate < values.serviceStartDate
    ) {
      window.alert(
        "Service end date cannot be before the start date.",
      );
      return;
    }

    setSaving(true);

    try {
      await onSave(values);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Edit client-facing details"
      description="Safe for paid invoices: this updates only the client snapshot, service coverage, public note, and payment QR visibility—not amounts or payment status."
      size="lg"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <FieldLabel required htmlFor="meta-client-name">
            Client name
          </FieldLabel>
          <Input
            id="meta-client-name"
            value={values.client.name}
            onChange={(event) =>
              setClient("name", event.target.value)
            }
          />
        </div>

        <div>
          <FieldLabel htmlFor="meta-client-company">
            Company
          </FieldLabel>
          <Input
            id="meta-client-company"
            value={values.client.company}
            onChange={(event) =>
              setClient("company", event.target.value)
            }
          />
        </div>

        <div>
          <FieldLabel htmlFor="meta-client-email">
            Email
          </FieldLabel>
          <Input
            id="meta-client-email"
            type="email"
            value={values.client.email}
            onChange={(event) =>
              setClient("email", event.target.value)
            }
          />
        </div>

        <div>
          <FieldLabel htmlFor="meta-client-phone">
            Phone
          </FieldLabel>
          <Input
            id="meta-client-phone"
            value={values.client.phone}
            onChange={(event) =>
              setClient("phone", event.target.value)
            }
          />
        </div>

        <div>
          <FieldLabel htmlFor="meta-service-start">
            Service from
          </FieldLabel>
          <Input
            id="meta-service-start"
            type="date"
            value={values.serviceStartDate}
            onChange={(event) =>
              setValues((current) => ({
                ...current,
                serviceStartDate: event.target.value,
              }))
            }
          />
        </div>

        <div>
          <FieldLabel htmlFor="meta-service-end">
            Service to
          </FieldLabel>
          <Input
            id="meta-service-end"
            type="date"
            value={values.serviceEndDate}
            onChange={(event) =>
              setValues((current) => ({
                ...current,
                serviceEndDate: event.target.value,
              }))
            }
          />
        </div>

        <PaymentQrOptionsField
          value={values.paymentQr}
          currency={currency}
          assets={paymentQrAssets}
          disabled={saving}
          onChange={(paymentQr) =>
            setValues((current) => ({
              ...current,
              paymentQr,
            }))
          }
        />

        <div className="sm:col-span-2">
          <ClientFacingNotesField
            id="meta-public-notes"
            value={values.publicNotes}
            disabled={saving}
            onChange={(publicNotes) =>
              setValues((current) => ({
                ...current,
                publicNotes,
              }))
            }
          />
        </div>
      </div>

      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          variant="secondary"
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
        <Button
          variant="brand"
          onClick={submit}
          disabled={saving}
        >
          <Save className="h-4 w-4" />
          {saving ? "Saving…" : "Save details"}
        </Button>
      </div>
    </Modal>
  );
}
